/**
 * =============================================================================
 * Mock Property Data
 * =============================================================================
 * Version: 1.0.0
 * Last Modified: 2025-03-02
 * 
 * Mock data based on Icnea API structure.
 * Used for local design testing before connecting to real API.
 * 
 * =============================================================================
 * DATA STRUCTURE (Based on Icnea API)
 * =============================================================================
 * - lodging_id, lodging_name, lodging_type
 * - address, city, region, country
 * - maximum_capacity, number_of_rooms, number_of_bathrooms
 * - minimum_rate, currency
 * - lodging_equipment (amenities)
 * - images, rating, review
 * 
 * =============================================================================
 * TEST ENVIRONMENT
 * =============================================================================
 * - Container: sunmarbella_test
 * - Database: sunmarbella_db_test
 * - Network: sunmarbella_test_net
 * 
 * =============================================================================
 * CHANGELOG
 * =============================================================================
 * - 1.0.0 (2025-03-02): Initial mock data
 * =============================================================================
 */

// =============================================================================
// TYPES (Matching Icnea API Structure)
// =============================================================================

export interface MockProperty {
  lodging_id: string;
  lodging_name: string;
  lodging_type: number;
  acronym: string;
  address: string;
  zip: string;
  city: string;
  region: string;
  country: string;
  country_name: string;
  telephone: string;
  email: string;
  latitude: number;
  longitude: number;
  maximum_capacity: number;
  number_of_rooms: number;
  number_of_bathrooms: number;
  number_of_toilets: number;
  number_of_single_beds: number;
  number_of_double_beds: number;
  number_of_sofa_beds: number;
  surface: number;
  surface_terrace: number;
  surface_garden: number;
  surface_pool: number;
  surface_unit: string;
  headline: string;
  description: string;
  category: string;
  minimum_rate: number;
  minimum_monthly_rate?: number;
  currency: string;
  license_number: string;
  online_booking: boolean;
  release: number;
  rating: number;
  review: number;
  distance_to_airport: number;
  distance_to_beach: number;
  distance_to_city_center: number;
  distance_unit: string;
  lodging_equipment: string[];
  lodging_location: string[];
  lodging_recommendations: string[];
  lodging_restrictions: string[];
  images: Array<{ url: string; caption: string; order: number }>;
}

// =============================================================================
// AMENITIES ICONS MAPPING
// =============================================================================

export const AMENITY_ICONS: Record<string, string> = {
  wifi: 'fa-wifi',
  free_wifi: 'fa-wifi',
  air_conditioning: 'fa-snowflake',
  heating: 'fa-fire',
  washing_machine: 'fa-shirt',
  dishwasher: 'fa-plate-wheat',
  tv: 'fa-tv',
  parking: 'fa-car',
  pool: 'fa-water-ladder',
  private_pool: 'fa-water-ladder',
  garden: 'fa-tree',
  terrace: 'fa-umbrella-beach',
  balcony: 'fa-building',
  sea_views: 'fa-water',
  mountain_views: 'fa-mountain',
  pets_allowed: 'fa-paw',
  bbq: 'fa-fire-burner',
  gym: 'fa-dumbbell',
  elevator: 'fa-elevator',
  wheelchair_accessible: 'fa-wheelchair',
  baby_friendly: 'fa-baby',
  family_friendly: 'fa-people-roof',
};

// =============================================================================
// PROPERTY TYPE MAPPING
// =============================================================================

export const PROPERTY_TYPES: Record<number, string> = {
  1: 'Apartment',
  2: 'House',
  3: 'Villa',
  4: 'Studio',
  5: 'Room',
  6: 'Penthouse',
  7: 'Townhouse',
};

// =============================================================================
// PLACEHOLDER IMAGES (Unsplash)
// =============================================================================

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&h=600&fit=crop',
];

// =============================================================================
// MOCK DATA - MARBELLA PROPERTIES
// =============================================================================

export const MOCK_PROPERTIES: MockProperty[] = [
  {
    lodging_id: '1001',
    lodging_name: 'Villa Luna del Mar',
    lodging_type: 3,
    acronym: 'VLM',
    address: 'Calle del Sol 15',
    zip: '29602',
    city: 'Marbella',
    region: 'Costa del Sol',
    country: 'ES',
    country_name: 'Spain',
    telephone: '+34 952 123 456',
    email: 'info@villaluna.com',
    latitude: 36.5108,
    longitude: -4.8867,
    maximum_capacity: 8,
    number_of_rooms: 4,
    number_of_bathrooms: 3,
    number_of_toilets: 4,
    number_of_single_beds: 2,
    number_of_double_beds: 3,
    number_of_sofa_beds: 1,
    surface: 250,
    surface_terrace: 80,
    surface_garden: 500,
    surface_pool: 40,
    surface_unit: 'm²',
    headline: 'Beachfront family rental with heated pool and sea breeze terraces',
    description: 'Villa Luna del Mar is designed for relaxed, sun-filled holidays right by the coast. Wake up to sea views, spend afternoons by the private pool, and enjoy outdoor dinners in a sheltered garden made for long evenings. The layout works beautifully for families, with spacious bedrooms, practical shared areas, and quick beach access for easy days out. Restaurants, beach clubs, and essentials are all close enough to keep your stay smooth and stress-free.',
    category: 'Luxury',
    minimum_rate: 450,
    minimum_monthly_rate: 8500,
    currency: 'EUR',
    license_number: 'VFT/MA/12345',
    online_booking: true,
    release: 2,
    rating: 4.8,
    review: 127,
    distance_to_airport: 45,
    distance_to_beach: 0.1,
    distance_to_city_center: 2,
    distance_unit: 'km',
    lodging_equipment: ['wifi', 'air_conditioning', 'heating', 'washing_machine', 'dishwasher', 'tv', 'parking', 'pool', 'bbq'],
    lodging_location: ['sea_views', 'beachfront'],
    lodging_recommendations: ['family_friendly', 'pets_allowed'],
    lodging_restrictions: ['smoking_not_allowed'],
    images: [
      { url: PLACEHOLDER_IMAGES[0], caption: 'Property exterior', order: 0 },
      { url: PLACEHOLDER_IMAGES[1], caption: 'Living room', order: 1 },
      { url: PLACEHOLDER_IMAGES[2], caption: 'Pool area', order: 2 },
      { url: PLACEHOLDER_IMAGES[3], caption: 'Master bedroom', order: 3 },
    ],
  },
  {
    lodging_id: '1002',
    lodging_name: 'Apartamento Playa Dorada',
    lodging_type: 1,
    acronym: 'APD',
    address: 'Paseo Marítimo 42, 3B',
    zip: '29603',
    city: 'Marbella',
    region: 'Costa del Sol',
    country: 'ES',
    country_name: 'Spain',
    telephone: '+34 952 234 567',
    email: 'reservas@playadorda.com',
    latitude: 36.5089,
    longitude: -4.8956,
    maximum_capacity: 4,
    number_of_rooms: 2,
    number_of_bathrooms: 1,
    number_of_toilets: 1,
    number_of_single_beds: 0,
    number_of_double_beds: 2,
    number_of_sofa_beds: 0,
    surface: 85,
    surface_terrace: 15,
    surface_garden: 0,
    surface_pool: 0,
    surface_unit: 'm²',
    headline: 'Stylish seaside apartment with balcony views over Marbella bay',
    description: 'Apartamento Playa Dorada is a bright coastal base for guests who want to be close to everything. The interiors are modern and comfortable, with a calm color palette and plenty of natural light. Enjoy morning coffee on the terrace, walk to the beach in minutes, and return to a practical, fully equipped apartment after exploring Marbella. Ideal for couples or small families looking for a convenient and well-located holiday home.',
    category: 'Standard',
    minimum_rate: 120,
    currency: 'EUR',
    license_number: 'VFT/MA/23456',
    online_booking: true,
    release: 1,
    rating: 4.5,
    review: 89,
    distance_to_airport: 48,
    distance_to_beach: 0.3,
    distance_to_city_center: 0.5,
    distance_unit: 'km',
    lodging_equipment: ['wifi', 'air_conditioning', 'tv', 'washing_machine'],
    lodging_location: ['sea_views', 'in_the_old_town'],
    lodging_recommendations: ['couples'],
    lodging_restrictions: ['pets_not_allowed', 'smoking_not_allowed'],
    images: [
      { url: PLACEHOLDER_IMAGES[4], caption: 'Apartment view', order: 0 },
      { url: PLACEHOLDER_IMAGES[5], caption: 'Bedroom', order: 1 },
      { url: PLACEHOLDER_IMAGES[6], caption: 'Kitchen', order: 2 },
    ],
  },
  {
    lodging_id: '1003',
    lodging_name: 'Casa del Olivo',
    lodging_type: 2,
    acronym: 'CDO',
    address: 'Urbanización Los Naranjos 8',
    zip: '29660',
    city: 'Nueva Andalucía',
    region: 'Costa del Sol',
    country: 'ES',
    country_name: 'Spain',
    telephone: '+34 952 345 678',
    email: 'olivo@rentals.com',
    latitude: 36.4925,
    longitude: -4.9478,
    maximum_capacity: 6,
    number_of_rooms: 3,
    number_of_bathrooms: 2,
    number_of_toilets: 3,
    number_of_single_beds: 2,
    number_of_double_beds: 2,
    number_of_sofa_beds: 0,
    surface: 180,
    surface_terrace: 40,
    surface_garden: 200,
    surface_pool: 25,
    surface_unit: 'm²',
    headline: 'Peaceful house near Golf Valley with private garden and pool',
    description: 'Casa del Olivo combines Andalusian charm with all the comforts you want on holiday. Set in a quiet residential area, the home offers generous indoor space, a sunny terrace, and a private pool surrounded by greenery. It is perfect for families or small groups who value privacy while staying close to Marbella, Puerto Banus, and top golf courses. The home feels warm, practical, and easy to settle into from the first day.',
    category: 'Premium',
    minimum_rate: 280,
    minimum_monthly_rate: 5200,
    currency: 'EUR',
    license_number: 'VFT/MA/34567',
    online_booking: true,
    release: 3,
    rating: 4.7,
    review: 156,
    distance_to_airport: 50,
    distance_to_beach: 3,
    distance_to_city_center: 5,
    distance_unit: 'km',
    lodging_equipment: ['wifi', 'air_conditioning', 'heating', 'washing_machine', 'dishwasher', 'tv', 'parking', 'pool', 'garden', 'bbq'],
    lodging_location: ['mountain_views', 'golf_nearby'],
    lodging_recommendations: ['family_friendly', 'pets_allowed'],
    lodging_restrictions: [],
    images: [
      { url: PLACEHOLDER_IMAGES[7], caption: 'House exterior', order: 0 },
      { url: PLACEHOLDER_IMAGES[0], caption: 'Garden', order: 1 },
      { url: PLACEHOLDER_IMAGES[1], caption: 'Living area', order: 2 },
      { url: PLACEHOLDER_IMAGES[2], caption: 'Pool', order: 3 },
    ],
  },
  {
    lodging_id: '1004',
    lodging_name: 'Penthouse Marbella Centro',
    lodging_type: 6,
    acronym: 'PMC',
    address: 'Avenida Ricardo Soriano 28, Ático',
    zip: '29601',
    city: 'Marbella',
    region: 'Costa del Sol',
    country: 'ES',
    country_name: 'Spain',
    telephone: '+34 952 456 789',
    email: 'penthouse@marbella.com',
    latitude: 36.5098,
    longitude: -4.8823,
    maximum_capacity: 4,
    number_of_rooms: 2,
    number_of_bathrooms: 2,
    number_of_toilets: 2,
    number_of_single_beds: 0,
    number_of_double_beds: 2,
    number_of_sofa_beds: 1,
    surface: 120,
    surface_terrace: 60,
    surface_garden: 0,
    surface_pool: 0,
    surface_unit: 'm²',
    headline: 'Elegant city penthouse with panoramic terrace and open views',
    description: 'Penthouse Marbella Centro puts you in the heart of town while giving you privacy above it all. The standout terrace is ideal for sunset drinks and open-air dining, while the interior offers sleek finishes, two full bathrooms, and comfortable sleeping spaces. Beaches, shopping, and dining are all within walking distance, making this a strong choice for guests who want style and convenience in one address.',
    category: 'Luxury',
    minimum_rate: 350,
    currency: 'EUR',
    license_number: 'VFT/MA/45678',
    online_booking: true,
    release: 2,
    rating: 4.9,
    review: 64,
    distance_to_airport: 47,
    distance_to_beach: 0.4,
    distance_to_city_center: 0,
    distance_unit: 'km',
    lodging_equipment: ['wifi', 'air_conditioning', 'heating', 'tv', 'washing_machine', 'dishwasher', 'elevator'],
    lodging_location: ['sea_views', 'mountain_views', 'city_center'],
    lodging_recommendations: ['couples', 'business'],
    lodging_restrictions: ['pets_not_allowed', 'smoking_not_allowed', 'adults_only'],
    images: [
      { url: PLACEHOLDER_IMAGES[3], caption: 'Terrace view', order: 0 },
      { url: PLACEHOLDER_IMAGES[4], caption: 'Living room', order: 1 },
      { url: PLACEHOLDER_IMAGES[5], caption: 'Bedroom', order: 2 },
    ],
  },
  {
    lodging_id: '1005',
    lodging_name: 'Estudio La Marina',
    lodging_type: 4,
    acronym: 'ELM',
    address: 'Puerto Deportivo 12',
    zip: '29604',
    city: 'Marbella',
    region: 'Costa del Sol',
    country: 'ES',
    country_name: 'Spain',
    telephone: '+34 952 567 890',
    email: 'marina@studios.com',
    latitude: 36.5045,
    longitude: -4.8912,
    maximum_capacity: 2,
    number_of_rooms: 1,
    number_of_bathrooms: 1,
    number_of_toilets: 1,
    number_of_single_beds: 0,
    number_of_double_beds: 1,
    number_of_sofa_beds: 0,
    surface: 45,
    surface_terrace: 8,
    surface_garden: 0,
    surface_pool: 0,
    surface_unit: 'm²',
    headline: 'Smart marina studio for couples steps from Puerto Banus life',
    description: 'Estudio La Marina is compact, comfortable, and brilliantly located for enjoying Marbella by day and night. The studio is set up for easy short stays with efficient interiors, a cozy sleeping area, and quick access to the promenade. Ideal for couples or solo travelers who want restaurants, beaches, and nightlife on their doorstep without needing a car.',
    category: 'Budget',
    minimum_rate: 75,
    currency: 'EUR',
    license_number: 'VFT/MA/56789',
    online_booking: true,
    release: 1,
    rating: 4.3,
    review: 203,
    distance_to_airport: 52,
    distance_to_beach: 0.5,
    distance_to_city_center: 6,
    distance_unit: 'km',
    lodging_equipment: ['wifi', 'air_conditioning', 'tv'],
    lodging_location: ['marina_views', 'near_restaurants'],
    lodging_recommendations: ['couples', 'solo_travelers'],
    lodging_restrictions: ['pets_not_allowed', 'smoking_not_allowed'],
    images: [
      { url: PLACEHOLDER_IMAGES[6], caption: 'Studio', order: 0 },
      { url: PLACEHOLDER_IMAGES[7], caption: 'View', order: 1 },
    ],
  },
  {
    lodging_id: '1006',
    lodging_name: 'Villa Andalucía Grande',
    lodging_type: 3,
    acronym: 'VAG',
    address: 'Sierra Blanca Alto 5',
    zip: '29610',
    city: 'Marbella',
    region: 'Costa del Sol',
    country: 'ES',
    country_name: 'Spain',
    telephone: '+34 952 678 901',
    email: 'andalucia@villagrande.com',
    latitude: 36.5178,
    longitude: -4.9123,
    maximum_capacity: 12,
    number_of_rooms: 6,
    number_of_bathrooms: 5,
    number_of_toilets: 6,
    number_of_single_beds: 4,
    number_of_double_beds: 4,
    number_of_sofa_beds: 2,
    surface: 450,
    surface_terrace: 150,
    surface_garden: 2000,
    surface_pool: 80,
    surface_unit: 'm²',
    headline: 'Grand hillside estate with pool, gardens, and sea panoramas',
    description: 'Villa Andalucia Grande is built for standout group stays, celebrations, and extended family holidays. With six bedrooms, expansive terraces, and wide Mediterranean views, the property offers space to gather without ever feeling crowded. Enjoy the large pool, private gardens, and multiple lounging areas, all in one of Marbella\'s most prestigious hillside settings. This is a true flagship villa for guests who expect scale, privacy, and top-tier comfort.',
    category: 'Ultra Luxury',
    minimum_rate: 10000,
    minimum_monthly_rate: 250000,
    currency: 'EUR',
    license_number: 'VFT/MA/67890',
    online_booking: true,
    release: 7,
    rating: 5.0,
    review: 42,
    distance_to_airport: 48,
    distance_to_beach: 4,
    distance_to_city_center: 3,
    distance_unit: 'km',
    lodging_equipment: ['wifi', 'air_conditioning', 'heating', 'washing_machine', 'dishwasher', 'tv', 'parking', 'pool', 'garden', 'bbq', 'gym', 'cinema_room'],
    lodging_location: ['sea_views', 'mountain_views', 'gated_community'],
    lodging_recommendations: ['family_friendly', 'groups', 'events'],
    lodging_restrictions: ['smoking_not_allowed'],
    images: [
      { url: PLACEHOLDER_IMAGES[0], caption: 'Property aerial', order: 0 },
      { url: PLACEHOLDER_IMAGES[1], caption: 'Main entrance', order: 1 },
      { url: PLACEHOLDER_IMAGES[2], caption: 'Infinity pool', order: 2 },
      { url: PLACEHOLDER_IMAGES[3], caption: 'Master suite', order: 3 },
      { url: PLACEHOLDER_IMAGES[4], caption: 'Gardens', order: 4 },
    ],
  },
];

// =============================================================================
// MOCK CITIES
// =============================================================================

export const MOCK_CITIES = [
  { city: 'Marbella', country: 'Spain', propertyCount: 156 },
  { city: 'Nueva Andalucía', country: 'Spain', propertyCount: 89 },
  { city: 'Puerto Banús', country: 'Spain', propertyCount: 67 },
  { city: 'San Pedro de Alcántara', country: 'Spain', propertyCount: 45 },
  { city: 'Estepona', country: 'Spain', propertyCount: 78 },
  { city: 'Benahavís', country: 'Spain', propertyCount: 34 },
];

// =============================================================================
// MOCK CALENDAR DATA
// =============================================================================

export function generateMockCalendar(propertyId: string, months: number = 3): Array<{
  date: string;
  available: boolean;
  minimum_stay: number;
  price?: number;
}> {
  const calendar = [];
  const today = new Date();
  const endDate = new Date(today);
  endDate.setMonth(endDate.getMonth() + months);

  const currentDate = new Date(today);
  while (currentDate <= endDate) {
    // Random availability (80% available)
    const available = Math.random() > 0.2;
    // Random minimum stay (1-7 nights)
    const minimum_stay = Math.floor(Math.random() * 7) + 1;
    // Random price variation
    const basePrice = 100 + Math.random() * 400;

    calendar.push({
      date: currentDate.toISOString().split('T')[0],
      available,
      minimum_stay,
      price: Math.round(basePrice),
    });

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return calendar;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getPropertyById(id: string): MockProperty | undefined {
  return MOCK_PROPERTIES.find(p => p.lodging_id === id);
}

export function searchProperties(params: {
  location?: string;
  guests?: number;
  bedrooms?: number;
  priceMin?: number;
  priceMax?: number;
}): MockProperty[] {
  return MOCK_PROPERTIES.filter(p => {
    if (params.location) {
      const loc = params.location.toLowerCase();
      if (!p.city.toLowerCase().includes(loc) && 
          !p.region.toLowerCase().includes(loc)) {
        return false;
      }
    }
    if (params.guests && p.maximum_capacity < params.guests) return false;
    if (params.bedrooms && p.number_of_rooms < params.bedrooms) return false;
    if (params.priceMin && p.minimum_rate < params.priceMin) return false;
    if (params.priceMax && p.minimum_rate > params.priceMax) return false;
    return true;
  });
}

export function formatPrice(price: number, currency: string = 'EUR'): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(price);
}
